import { useMemo } from "react";
import { Fields, type TypedWrappedFieldProps } from "redux-form";

import type { EnhanceElementProps } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  GraterOrEqualZeroNumberFormat,
  MenuSelect,
  NumberInput,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { MenuArrowDownIcon } from "@braintrust/braintrust-ui-components/Icons";
import { FieldSectionPanelWithImportance } from "@js/apps/jobs/forms/fields/field-section-panel";
import { violetMenuItemSx } from "@js/components/violet-menu-item";
import { TextareaField } from "@js/forms/fields";
import { shouldDisplayError } from "@js/forms/utils";

import { OFFER_FIELDS } from "../../constants";
import { useCreateOrEditOfferMeta } from "../../hooks/create-or-edit-offer-meta";
import type { CreateOfferFormData } from "../../types";
import { getLabelForWorkingHourOption } from "../../utils";

const MAX_WEEKLY_HOURS = 168;
const MAX_HOURS = 100000;

const validateWorkingHours = (_value: unknown, values: CreateOfferFormData) => {
  const hoursValue = Number(values.anticipated_weekly_hours);
  const hoursType = values.hours_limit_type;
  const weeklyHoursType: Array<EnumType<typeof ENUMS.HoursLimitType>> = [
    ENUMS.HoursLimitType.PER_WEEK,
    ENUMS.HoursLimitType.UP_TO_PER_WEEK,
  ];
  if (weeklyHoursType.includes(hoursType) && hoursValue > MAX_WEEKLY_HOURS) {
    return `The maximum hourly commitment cannot exceed ${MAX_WEEKLY_HOURS} hours.`;
  }
  if (hoursValue > MAX_HOURS) {
    return `The maximum hourly commitment cannot exceed ${MAX_HOURS} hours.`;
  }
};

type WorkingHoursSectionProps = { isDirectHireJob?: boolean };

export const WorkingHoursSection = ({
  isDirectHireJob,
}: WorkingHoursSectionProps) => {
  return (
    <Fields
      names={[
        OFFER_FIELDS.anticipated_weekly_hours,
        OFFER_FIELDS.hours_limit_type,
        OFFER_FIELDS.pre_approve_extra_hours,
        OFFER_FIELDS.availability_details,
      ]}
      component={WorkingHoursSectionComponent}
      isDirectHireJob={!!isDirectHireJob}
      validate={{
        [OFFER_FIELDS.anticipated_weekly_hours]: validateWorkingHours,
      }}
    />
  );
};

type WorkingHoursSectionComponentProps = {
  anticipated_weekly_hours: TypedWrappedFieldProps<string>;
  hours_limit_type: TypedWrappedFieldProps<
    EnumType<typeof ENUMS.HoursLimitType>
  >;
  pre_approve_extra_hours: TypedWrappedFieldProps<boolean>;
  availability_details: TypedWrappedFieldProps<string>;
  isDirectHireJob: boolean;
};

export const WorkingHoursSectionComponent = ({
  anticipated_weekly_hours,
  hours_limit_type,
  pre_approve_extra_hours,
  availability_details,
  isDirectHireJob,
}: WorkingHoursSectionComponentProps) => {
  const displayError = shouldDisplayError([
    anticipated_weekly_hours,
    hours_limit_type,
    pre_approve_extra_hours,
    availability_details,
  ]);
  const offerMeta = useCreateOrEditOfferMeta();

  const hoursError = anticipated_weekly_hours.meta.error;
  const hoursTypeError = hours_limit_type.meta.error;
  const errorMessage: string | undefined = hoursError || hoursTypeError;

  return (
    <FieldSectionPanelWithImportance
      title="⌛ Commitment"
      fieldImportance="required"
      isError={displayError}
    >
      <Divider color="beige" sx={{ my: 2 }} />
      <Typography
        component="label"
        id="anticipated-hours-label"
        htmlFor="anticipated-hours"
        variant="paragraph"
        sx={{ overflowWrap: "anywhere", display: "block", mb: 2 }}
      >
        How many hours will {offerMeta?.talent_name} be working?
      </Typography>
      <Stack sx={{ mb: 1, gap: 1 }}>
        <WorkingHoursField
          anticipatedHours={anticipated_weekly_hours.input.value}
          anticipatedHoursOnChange={anticipated_weekly_hours.input.onChange}
          anticipatedHoursType={hours_limit_type.input.value}
          anticipatedHoursTypeOnChange={hours_limit_type.input.onChange}
          isDirectHireJob={isDirectHireJob}
          error={displayError && !!errorMessage}
        />
        {displayError && !!errorMessage && (
          <Typography component="p" variant="paragraph" error>
            {errorMessage}
          </Typography>
        )}
      </Stack>
      <Box sx={{ mb: 3 }}>
        <ExtraHoursApprovedField
          pre_approve_extra_hours={pre_approve_extra_hours}
        />
      </Box>
      <TextareaField
        {...availability_details}
        id="availability-details"
        label={
          <Typography
            variant="label"
            size="medium"
            fontWeight={500}
            component="span"
          >
            Additional availability details
            <Typography
              variant="paragraph"
              size="small"
              fontWeight={400}
              component="span"
              color="grey-2"
              sx={{ display: "inline-block", ml: 0.75 }}
            >
              Optional
            </Typography>
          </Typography>
        }
        placeholder="Add any other requirements"
        maxLength={500}
        maxLengthInHelpText
        sx={{ textarea: { p: "0 !important" } }}
        maxRows={8}
        minRows={4}
      />
    </FieldSectionPanelWithImportance>
  );
};

export type WorkingHoursFieldProps = {
  anticipatedHours: string;
  anticipatedHoursOnChange: (value: string) => void;
  anticipatedHoursType: EnumType<typeof ENUMS.HoursLimitType> | null;
  anticipatedHoursTypeOnChange: (
    value: EnumType<typeof ENUMS.HoursLimitType>,
  ) => void;
  isDirectHireJob?: boolean;
  error?: boolean;
};

export const WorkingHoursField = ({
  anticipatedHoursOnChange,
  anticipatedHoursType,
  anticipatedHoursTypeOnChange,
  anticipatedHours,
  isDirectHireJob,
  error,
}: WorkingHoursFieldProps) => {
  const hoursType = anticipatedHoursType || ENUMS.HoursLimitType.PER_WEEK;
  const options = useMemo(() => {
    return getOptions({
      anticipatedHours,
      isDirectHireJob,
    });
  }, [anticipatedHours, isDirectHireJob]);
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
      <Box sx={{ width: 100 }}>
        <NumberInput
          id="anticipated-hours"
          size="large"
          NumberFormat={GraterOrEqualZeroNumberFormat}
          error={error}
          value={anticipatedHours}
          onChange={anticipatedHoursOnChange}
          slotProps={{
            input: {
              style: { boxShadow: `var(--elevation-4)`, borderRadius: "9px" },
            },
          }}
        />
      </Box>
      <MenuSelect
        value={hoursType}
        onChange={(value) => {
          if (!value) {
            return;
          }
          anticipatedHoursTypeOnChange(
            value as EnumType<typeof ENUMS.HoursLimitType>,
          );
        }}
        menuItemSx={violetMenuItemSx}
        disableMenuItemRipple
        type="radio"
        anchor={(props) =>
          renderAnchor({
            props,
            anticipatedHoursType: hoursType,
            anticipatedHours,
          })
        }
        sx={{ li: { padding: "10px 12px !important" } }}
        options={options}
      />
    </Stack>
  );
};

const getOptions = ({
  anticipatedHours,
  isDirectHireJob,
}: {
  anticipatedHours: string;
  isDirectHireJob?: boolean;
}) => {
  const options = Object.values(ENUMS.HoursLimitType).map(
    (anticipatedHoursType) => {
      return {
        value: anticipatedHoursType,
        label: getLabelForWorkingHourOption({
          option: anticipatedHoursType,
          anticipatedHours,
        }),
      };
    },
  );

  if (isDirectHireJob) {
    return options.filter(
      (option) => option.value !== ENUMS.HoursLimitType.MAX_TOTAL,
    );
  }

  return options;
};

type AnchorProps = {
  anchorProps: EnhanceElementProps;
  isOpen: boolean;
};

const renderAnchor = ({
  props,
  anticipatedHoursType,
  anticipatedHours,
}: {
  props: AnchorProps;
  anticipatedHoursType: EnumType<typeof ENUMS.HoursLimitType>;
  anticipatedHours: string;
}) => {
  const { anchorProps, isOpen } = props;
  const {
    "aria-controls": ariaControls,
    "aria-haspopup": ariaHasPopup,
    ...rest
  } = anchorProps;

  return (
    <Button
      aria-controls={ariaControls}
      aria-haspopup={ariaHasPopup}
      style={{ padding: "6px 8px" }}
      shape="squared"
      variant="transparent-with-text-hover"
      aria-label="Select hours type"
      {...rest}
    >
      {getLabelForWorkingHourOption({
        option: anticipatedHoursType,
        anticipatedHours,
      })}
      <Box sx={{ ml: 1, pt: 0.5 }}>
        <MenuArrowDownIcon
          sx={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </Box>
    </Button>
  );
};

type ExtraHoursApprovedFieldProps = Pick<
  WorkingHoursSectionComponentProps,
  "pre_approve_extra_hours"
>;
const ExtraHoursApprovedField = ({
  pre_approve_extra_hours,
}: ExtraHoursApprovedFieldProps) => {
  return (
    <Checkbox
      label="Extra hours must be pre-approved"
      checked={!!pre_approve_extra_hours.input.value}
      onChange={(_ev, checked) => {
        pre_approve_extra_hours.input.onChange(checked);
      }}
    />
  );
};
